import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Activity, Server, Tv, Globe2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { streamFlowApi } from '@lib/streamFlowApi';
import type { Provider, MeInfo, ProxyNode } from '@lib/streamFlowApi';
import { supabase } from '@lib/supabase';

export default function StreamProviders() {
  const navigate = useNavigate();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [proxyNodes, setProxyNodes] = useState<ProxyNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [me, setMe] = useState<MeInfo | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    provider_type: 'xtream' as const,
    host: '',
    username: '',
    password: '',
    max_connections: 1,
    // Admin-only field; default true for admins so the community model
    // is opt-out rather than opt-in (the whole point is sharing).
    is_shared: true,
    // Provider-egress pin. Defaults to the household's default-community
    // proxy node once we've fetched the list. Required for new providers.
    bound_proxy_node_id: '' as string,
  });

  const fetchProviders = async () => {
    const res = await streamFlowApi.listProviders();
    if (res.success) setProviders(res.data || []);
    setLoading(false);
  };

  useEffect(() => {
    Promise.all([
      streamFlowApi.getMe(),
      supabase.auth.getUser(),
      streamFlowApi.listProxyNodes(),
    ]).then(([meRes, userRes, nodesRes]) => {
      if (meRes.success && meRes.data) setMe(meRes.data);
      setCurrentUserId(userRes.data.user?.id ?? null);
      if (nodesRes.success && nodesRes.data) {
        setProxyNodes(nodesRes.data);
        // Preselect the household's default-community node so the
        // user can't easily create a provider with NULL egress (the
        // resolver fall-back) by accident.
        const def = nodesRes.data.find(n => n.is_default_community);
        if (def) {
          setFormData(d => d.bound_proxy_node_id ? d : { ...d, bound_proxy_node_id: def.id });
        }
      }
    });
    fetchProviders();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.bound_proxy_node_id) {
      toast.error('Pick a proxy node — all viewers of this provider will egress through it');
      return;
    }
    const base: any = me?.is_admin ? formData : { ...formData, is_shared: undefined };
    const payload = { ...base, bound_proxy_node_id: formData.bound_proxy_node_id };
    const res = await streamFlowApi.createProvider(payload);
    if (res.success) {
      toast.success('Provider added!');
      setShowForm(false);
      setFormData({ name: '', provider_type: 'xtream', host: '', username: '', password: '', max_connections: 1, is_shared: true, bound_proxy_node_id: formData.bound_proxy_node_id });
      fetchProviders();
    } else {
      toast.error(res.error?.message || 'Failed to add provider');
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete provider "${name}"? This will remove all associated seats.`)) return;
    const res = await streamFlowApi.deleteProvider(id);
    if (res.success) {
      toast.success('Provider deleted');
      fetchProviders();
    } else {
      toast.error(res.error?.message || 'Failed to delete');
    }
  };

  const handleHealthCheck = async (id: string) => {
    toast.loading('Checking...', { id: 'health' });
    const res = await streamFlowApi.checkProviderHealth(id);
    if (res.success) {
      // The /health-check endpoint returns { healthy: boolean, ... }; the
      // legacy toast read `health_status` (the column name) which is
      // never present, so it always said "checked" regardless of result.
      // Now we actually read the boolean.
      const data = res.data as { healthy?: boolean; error?: string } | undefined;
      if (data?.healthy === true) {
        toast.success('Provider is healthy', { id: 'health' });
      } else if (data?.healthy === false) {
        toast.error(`Provider is unhealthy${data.error ? ` — ${data.error}` : '. Check the host URL and credentials.'}`, { id: 'health', duration: 8000 });
      } else {
        toast.success('Provider checked', { id: 'health' });
      }
      fetchProviders();
    } else {
      toast.error(res.error?.message || 'Health check failed', { id: 'health' });
    }
  };

  const handleSyncChannels = async (id: string, name: string) => {
    toast.loading(`Starting channel sync for ${name}...`, { id: 'sync' });
    const res = await streamFlowApi.syncChannels(id);
    if (res.success) {
      // Server returns 202 Accepted immediately — sync continues in the
      // background. Xtream lists with thousands of channels can take 30-60s
      // to fully populate; user refreshes the dashboard to see results.
      toast.success(`Sync started for ${name}. Channels will appear in 30-60 seconds — refresh the dashboard to see them.`, {
        id: 'sync',
        duration: 8000,
      });
    } else {
      toast.error(res.error?.message || 'Failed to start sync', { id: 'sync' });
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <button onClick={() => navigate('/')} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Dashboard
      </button>

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">IPTV Providers</h1>
        <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 text-sm">
          <Plus className="w-4 h-4" /> Add Provider
        </button>
      </div>

      {/* Add Provider Form */}
      {showForm && (
        <form onSubmit={handleCreate} className="bg-white border rounded-lg p-6 mb-6 space-y-4">
          <h3 className="font-semibold">New Provider</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <input type="text" required value={formData.name} onChange={e => setFormData(d => ({ ...d, name: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="My IPTV Provider" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Type</label>
              <select value={formData.provider_type} onChange={e => setFormData(d => ({ ...d, provider_type: e.target.value as any }))}
                className="w-full px-3 py-2 border rounded-lg text-sm">
                <option value="xtream">Xtream Codes</option>
                <option value="m3u">M3U Playlist</option>
                <option value="jellyfin">Jellyfin</option>
                <option value="ersatztv">ErsatzTV</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium mb-1">Host URL</label>
              <input type="text" required value={formData.host} onChange={e => setFormData(d => ({ ...d, host: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="http://provider.example.com:8080" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Username</label>
              <input type="text" value={formData.username} onChange={e => setFormData(d => ({ ...d, username: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Password</label>
              <input type="password" value={formData.password} onChange={e => setFormData(d => ({ ...d, password: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Max Connections (Seats)</label>
              <input type="number" min={1} max={50} value={formData.max_connections} onChange={e => setFormData(d => ({ ...d, max_connections: parseInt(e.target.value) || 1 }))}
                className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium mb-1">Egress proxy node <span className="text-red-600">*</span></label>
              <p className="text-xs text-gray-500 mb-1.5">
                All viewers of this provider egress through this one machine — keeps upstream creds tied to a single IP so the provider doesn't ban us.
              </p>
              <select
                required
                value={formData.bound_proxy_node_id}
                onChange={e => setFormData(d => ({ ...d, bound_proxy_node_id: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg text-sm"
              >
                <option value="" disabled>Choose a proxy node…</option>
                {proxyNodes.map(n => (
                  <option key={n.id} value={n.id}>
                    {n.name} ({n.host}) {n.health_status !== 'healthy' ? `[${n.health_status}]` : ''}
                  </option>
                ))}
              </select>
              {proxyNodes.length === 0 && (
                <p className="text-xs text-amber-700 mt-1">
                  No proxy nodes available. Add one in <a href="/settings/proxy-nodes" className="text-blue-600 hover:underline">Proxy Nodes →</a> first.
                </p>
              )}
            </div>
          </div>

          {/* Admin-only: opt out of community sharing on this provider. */}
          {me?.is_admin && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={formData.is_shared}
                onChange={e => setFormData(d => ({ ...d, is_shared: e.target.checked }))}
                className="rounded"
              />
              <span className="flex items-center gap-1.5">
                <Globe2 className="w-4 h-4 text-blue-600" />
                Share with all family users (community provider)
              </span>
            </label>
          )}

          <div className="flex gap-3">
            <button type="submit" className="px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 text-sm">Save Provider</button>
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Cancel</button>
          </div>
        </form>
      )}

      {/* Provider List */}
      {loading ? (
        <p className="text-gray-500 text-center py-8">Loading providers...</p>
      ) : providers.length === 0 ? (
        <div className="text-center py-16 bg-white border rounded-lg">
          <Server className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No providers configured yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {providers.map(p => {
            const isOwner = currentUserId !== null && p.user_id === currentUserId;
            // Sync + health-check are owner-or-admin actions (require creds);
            // delete is strictly owner. Non-owner users on shared providers
            // see only an info-level row with the community badge.
            const canManage = isOwner || (me?.is_admin && p.is_shared);
            return (
              <div key={p.id} className="bg-white border rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-3 h-3 rounded-full ${
                    p.health_status === 'healthy' ? 'bg-green-500' :
                    p.health_status === 'unhealthy' ? 'bg-red-500' : 'bg-gray-300'
                  }`} />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{p.name}</p>
                      {p.is_shared && (
                        <span
                          title={isOwner ? 'You are sharing this provider with all users' : 'Community provider — shared by an admin'}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200"
                        >
                          <Globe2 className="w-3 h-3" />
                          Community
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">{p.provider_type} &middot; {p.max_connections} seat{p.max_connections > 1 ? 's' : ''} &middot; {p.host}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {canManage && (
                    <button onClick={() => handleSyncChannels(p.id, p.name)} className="p-2 hover:bg-gray-100 rounded-lg" title="Sync channels into the guide">
                      <Tv className="w-4 h-4" />
                    </button>
                  )}
                  {canManage && (
                    <button onClick={() => handleHealthCheck(p.id)} className="p-2 hover:bg-gray-100 rounded-lg" title="Health Check">
                      <Activity className="w-4 h-4" />
                    </button>
                  )}
                  {isOwner && (
                    <button onClick={() => handleDelete(p.id, p.name)} className="p-2 hover:bg-red-50 rounded-lg text-red-500" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
